import { useCallback, useRef, useState } from 'react';
import * as Y from 'yjs';
import { strokeOptions } from "@/features/canvas/engine/primitives/geometry/path";
import { createNode } from "@/features/canvas/engine/primitives/persistence/create-node";
import { useScreenToCanvasCoords } from '../hooks/use-screen-to-canvas-coords';
import getStroke from 'perfect-freehand';
import styles from './drawing-overlay.module.css';

interface Props {
  doc: Y.Doc;
  strokeColor?: string;
  strokeWidth?: number;
  strokeOpacity?: number;
}

// [x, y, pressure]
type Point = [number, number, number];

/**
 * Full-canvas pointer capture overlay for freehand drawing.
 * Mounts inside <ReactFlow> so it can use useReactFlow().screenToFlowPosition.
 *
 * Two coordinate spaces are tracked separately:
 * - screenPoints: raw clientX/Y used for the live SVG preview (screen pixels,
 *   matches the absolute-positioned SVG overlay 1:1)
 * - canvasPoints: screenToFlowPosition output used for the committed Yjs node
 */
export function DrawingOverlay({ doc, strokeColor = '#2D3134', strokeWidth = 2, strokeOpacity = 1 }: Props) {
  const screenToCanvas = useScreenToCanvasCoords();

  // Keep current stroke props in refs so onPointerUp always reads the latest
  // values without being re-created on every prop change.
  const strokeColorRef = useRef(strokeColor);
  const strokeWidthRef = useRef(strokeWidth);
  const strokeOpacityRef = useRef(strokeOpacity);
  strokeColorRef.current = strokeColor;
  strokeWidthRef.current = strokeWidth;
  strokeOpacityRef.current = strokeOpacity;

  // Screen-space points for the live preview SVG
  const [screenPoints, setScreenPoints] = useState<Point[]>([]);

  const drawingRef = useRef(false);
  // Canvas-space points accumulated for the Yjs commit
  const canvasPointsRef = useRef<Point[]>([]);
  // Screen-space points mirrored in a ref so onPointerUp can read without stale closure
  const screenPointsRef = useRef<Point[]>([]);

  const onPointerDown = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const pressure = e.pressure === 0 ? 0.5 : e.pressure;

    const rect = e.currentTarget.getBoundingClientRect();
    const screen: Point = [e.clientX - rect.left, e.clientY - rect.top, pressure];
    const canvas = screenToCanvas(e.clientX, e.clientY);
    const canvasPt: Point = [canvas.x, canvas.y, pressure];

    screenPointsRef.current = [screen];
    canvasPointsRef.current = [canvasPt];
    setScreenPoints([screen]);
  }, [screenToCanvas]);

  const onPointerMove = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (!drawingRef.current) return;
    const pressure = e.pressure === 0 ? 0.5 : e.pressure;

    const rect = e.currentTarget.getBoundingClientRect();
    const screen: Point = [e.clientX - rect.left, e.clientY - rect.top, pressure];
    const canvas = screenToCanvas(e.clientX, e.clientY);
    const canvasPt: Point = [canvas.x, canvas.y, pressure];

    screenPointsRef.current = [...screenPointsRef.current, screen];
    canvasPointsRef.current = [...canvasPointsRef.current, canvasPt];
    setScreenPoints(prev => [...prev, screen]);
  }, [screenToCanvas]);

  const onPointerUp = useCallback(() => {
    if (!drawingRef.current) return;
    drawingRef.current = false;

    const pts = canvasPointsRef.current;
    if (pts.length < 2) { setScreenPoints([]); return; }

    // Compute bounding box in canvas space
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [x, y] of pts) {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
    const w = Math.max(maxX - minX, 1);
    const h = Math.max(maxY - minY, 1);

    // Translate to node-local coordinates
    const localPts: Point[] = pts.map(([x, y, p]) => [x - minX, y - minY, p]);

    createNode(
      doc,
      'freedraw',
      { x: minX, y: minY },
      { points: localPts, initialSize: { width: w, height: h }, strokeColor: strokeColorRef.current, strokeWidth: strokeWidthRef.current, strokeOpacity: strokeOpacityRef.current },
      doc.clientID,
      { width: w, height: h },
    );

    setScreenPoints([]);
    screenPointsRef.current = [];
    canvasPointsRef.current = [];
  }, [doc]);

  // Live preview uses screen-space points — the SVG is position:absolute over
  // the RF container in screen pixels, so screen coordinates map 1:1.
  const previewPath = screenPoints.length > 1
    ? (() => {
        const stroke = getStroke(screenPoints, strokeOptions(strokeWidth));
        if (!stroke.length) return '';
        const d = stroke.reduce(
          (acc, [x0, y0], i, arr) => {
            const [x1, y1] = arr[(i + 1) % arr.length];
            acc.push(x0, y0, ',', (x0 + x1) / 2, (y0 + y1) / 2);
            return acc;
          },
          ['M', ...stroke[0], 'Q'] as (string | number)[],
        );
        d.push('Z');
        return d.join(' ');
      })()
    : '';

  return (
    <svg
      className={styles.overlay}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {previewPath && (
        <path d={previewPath} fill={strokeColor} opacity={strokeOpacity * 0.8} />
      )}
    </svg>
  );
}
